import { DmnModel, ColumnDefinition, Rule, HitPolicy, ColumnType, DataType } from '../types';
import { validateModel, validateCell } from './validation';

/**
 * EditorAiCapability — the structural contract the admin AI assistant consumes.
 *
 * Defined LOCALLY (no dependency on the admin app) so this editor stays standalone.
 * The shapes mirror the admin's `types/ai.ts` (EditorAiCapability / EditorToolResult):
 * while the editor is focused, its `tools` join the assistant's tool set, and calling
 * one runs `invoke` INSIDE the editor (client-side), which validates and may reject.
 */
export interface EditorToolResult {
  ok: boolean;
  result?: unknown;
  rejected?: boolean;
  reason?: string;
}

export interface EditorToolSpec {
  name: string;
  description: string;
  /** JSON Schema object describing the tool's arguments. */
  parameters: Record<string, unknown>;
}

export interface EditorAiCapability {
  kind: string;
  guidelines: string;
  tools: EditorToolSpec[];
  invoke: (toolName: string, args: Record<string, unknown>) => Promise<EditorToolResult>;
}

/** The live model accessors the capability needs from the editor. */
export interface DmnModelAccess {
  /** Always returns the LATEST model (read through a ref, not a stale closure). */
  getModel: () => DmnModel | null;
  /** Applies a new model live (re-renders the editor). Same path the UI uses. */
  setModel: (model: DmnModel) => void;
}

const COLUMN_TYPES: ColumnType[] = ['input', 'output', 'annotation'];
const DATA_TYPES: DataType[] = ['string', 'number', 'boolean', 'date'];
const HIT_POLICIES: HitPolicy[] = ['UNIQUE', 'FIRST', 'PRIORITY', 'ANY', 'COLLECT', 'RULE ORDER', 'OUTPUT ORDER'];

const ok = (result?: unknown): EditorToolResult => ({ ok: true, result });
const reject = (reason: string): EditorToolResult => ({ ok: false, rejected: true, reason });

/** Compact JSON view of the model — small, model-aware, no UI noise. */
const compactModel = (m: DmnModel) => ({
  id: m.id,
  name: m.name,
  hitPolicy: m.hitPolicy,
  columns: m.columns.map((c) => ({
    id: c.id,
    label: c.label,
    type: c.type,
    dataType: c.dataType,
    variableName: c.variableName,
  })),
  rules: m.rules.map((r) => ({
    id: r.id,
    cells: m.columns.reduce<Record<string, string>>((acc, c) => {
      acc[c.id] = r.cells[c.id] ?? '';
      return acc;
    }, {}),
  })),
});

let idCounter = 0;
const freshId = (prefix: string) => `${prefix}${Date.now()}_${idCounter++}`;

/**
 * Run the editor's REAL validator on a candidate model and describe its invalid cells.
 * Maps each invalidCells key (`${ruleId}-${colId}`) back to a human-readable cell location.
 */
const describeInvalidCells = (model: DmnModel, invalidCells: Record<string, boolean>): string[] => {
  const colById = new Map(model.columns.map((c) => [c.id, c]));
  return Object.keys(invalidCells).map((key) => {
    const dash = key.lastIndexOf('-');
    const ruleId = key.slice(0, dash);
    const colId = key.slice(dash + 1);
    const col = colById.get(colId);
    const value = model.rules.find((r) => r.id === ruleId)?.cells[colId] ?? '';
    const where = col ? `column "${col.label}" (${col.dataType})` : `column "${colId}"`;
    return `rule "${ruleId}" / ${where}: value "${value}" is invalid`;
  });
};

/**
 * Apply `next` ONLY if it does not introduce validation errors that the current model did not
 * already have; otherwise reject with the validator's reasons. This is the single gate every
 * mutating tool routes through: candidate model in -> real validateModel -> apply or reject.
 */
const applyIfValid = (
  prev: DmnModel,
  next: DmnModel,
  setModel: (m: DmnModel) => void,
  result?: unknown,
): EditorToolResult => {
  const before = validateModel(prev);
  const after = validateModel(next);
  // Reject only when the change introduces NEW invalid cells (don't punish pre-existing errors).
  const newlyInvalid = Object.keys(after.invalidCells).filter((k) => !before.invalidCells[k]);
  if (newlyInvalid.length > 0) {
    const reasons = describeInvalidCells(next, Object.fromEntries(newlyInvalid.map((k) => [k, true])));
    return reject(`Change rejected: it introduces ${newlyInvalid.length} validation error(s): ${reasons.join('; ')}.`);
  }
  setModel(next);
  return ok(result);
};

/**
 * Build the DMN editor's AI capability over a live model accessor. Pure function (no React)
 * so it is unit-testable; the React hook just wires getModel/setModel to component state.
 *
 * Every mutating tool routes through the SAME semantics the DecisionTable UI uses and then
 * re-validates, so invalid input is rejected with a reason INSTEAD of corrupting the model.
 */
export function createDmnAiCapability(access: DmnModelAccess): EditorAiCapability {
  const tools: EditorToolSpec[] = [
    {
      name: 'get_model',
      description:
        'Return the current DMN decision table as compact JSON: id, name, hitPolicy, columns (input/output/annotation, each with dataType + variableName) and rules (cells keyed by column id).',
      parameters: { type: 'object', properties: {}, additionalProperties: false },
    },
    {
      name: 'list_element_types',
      description:
        'List the valid column types, data types and hit policies this DMN editor supports.',
      parameters: { type: 'object', properties: {}, additionalProperties: false },
    },
    {
      name: 'add_column',
      description:
        'Add an input, output or annotation column. Inputs are inserted before the first output (matching the editor). Returns the new column id.',
      parameters: {
        type: 'object',
        properties: {
          type: { type: 'string', enum: COLUMN_TYPES, description: 'Column type.' },
          label: { type: 'string', description: 'Human-readable header label.' },
          dataType: { type: 'string', enum: DATA_TYPES, description: 'FEEL data type. Defaults to string.' },
          variableName: { type: 'string', description: 'FEEL variable / output name.' },
        },
        required: ['type', 'label'],
        additionalProperties: false,
      },
    },
    {
      name: 'update_column',
      description: 'Update an existing column (label, dataType, variableName, type) by id.',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Column id to update.' },
          label: { type: 'string' },
          dataType: { type: 'string', enum: DATA_TYPES },
          variableName: { type: 'string' },
          type: { type: 'string', enum: COLUMN_TYPES },
        },
        required: ['id'],
        additionalProperties: false,
      },
    },
    {
      name: 'delete_column',
      description: 'Delete a column by id and drop its cells from every rule.',
      parameters: {
        type: 'object',
        properties: { id: { type: 'string', description: 'Column id to delete.' } },
        required: ['id'],
        additionalProperties: false,
      },
    },
    {
      name: 'add_rule',
      description:
        'Add a rule. Optionally provide cells (map of column id -> FEEL value). Unknown column ids and cells that fail the column dataType validation are rejected.',
      parameters: {
        type: 'object',
        properties: {
          cells: {
            type: 'object',
            description: 'Map of column id -> cell value (FEEL text). Omitted columns default to empty.',
            additionalProperties: { type: 'string' },
          },
        },
        additionalProperties: false,
      },
    },
    {
      name: 'update_rule',
      description:
        'Set cell values on an existing rule (map of column id -> value). Unknown column ids and values failing dataType validation are rejected.',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Rule id to update.' },
          cells: { type: 'object', additionalProperties: { type: 'string' } },
        },
        required: ['id', 'cells'],
        additionalProperties: false,
      },
    },
    {
      name: 'delete_rule',
      description: 'Delete a rule by id.',
      parameters: {
        type: 'object',
        properties: { id: { type: 'string', description: 'Rule id to delete.' } },
        required: ['id'],
        additionalProperties: false,
      },
    },
    {
      name: 'set_hit_policy',
      description: 'Set the decision table hit policy.',
      parameters: {
        type: 'object',
        properties: { hitPolicy: { type: 'string', enum: HIT_POLICIES } },
        required: ['hitPolicy'],
        additionalProperties: false,
      },
    },
    {
      name: 'validate',
      description:
        'Validate the whole model against the editor rules. Returns isValid, errorCount and the list of invalid {ruleId, columnId} cells.',
      parameters: { type: 'object', properties: {}, additionalProperties: false },
    },
    {
      name: 'replace_model',
      description:
        'Replace the ENTIRE model from JSON ({id,name,hitPolicy,columns,rules}). Validated structurally before applying; rejected if malformed.',
      parameters: {
        type: 'object',
        properties: {
          model: { type: 'object', description: 'A full DmnModel JSON object.' },
        },
        required: ['model'],
        additionalProperties: false,
      },
    },
  ];

  const guidelines =
    'This editor edits a single DMN decision table. The model is { id, name, hitPolicy, columns[], rules[] }. ' +
    `Columns have a type (${COLUMN_TYPES.join('/')}) and a dataType (${DATA_TYPES.join('/')}); inputs come before outputs. ` +
    'Rules hold cells keyed by column id (FEEL text; "" or "-" means wildcard). ' +
    `Valid hit policies: ${HIT_POLICIES.join(', ')}. ` +
    'Use get_model / list_element_types first. Mutations (add/update/delete column or rule, set_hit_policy, replace_model) ' +
    'are validated by the editor and rejected with a reason on unknown ids, bad types, or cell values that violate the column dataType. ' +
    'Use validate to surface current errors.';

  const invoke = async (
    toolName: string,
    args: Record<string, unknown>,
  ): Promise<EditorToolResult> => {
    const model = access.getModel();
    if (!model) return reject('The DMN model is not loaded yet.');
    const a = args || {};

    switch (toolName) {
      case 'get_model':
        return ok(compactModel(model));

      case 'list_element_types':
        return ok({ columnTypes: COLUMN_TYPES, dataTypes: DATA_TYPES, hitPolicies: HIT_POLICIES });

      case 'add_column': {
        const type = a.type as ColumnType;
        const label = a.label as string;
        if (!COLUMN_TYPES.includes(type)) return reject(`Unknown column type "${String(a.type)}". Valid: ${COLUMN_TYPES.join(', ')}.`);
        if (typeof label !== 'string' || label.trim() === '') return reject('label is required and must be a non-empty string.');
        const dataType = (a.dataType as DataType) ?? 'string';
        if (!DATA_TYPES.includes(dataType)) return reject(`Unknown dataType "${String(a.dataType)}". Valid: ${DATA_TYPES.join(', ')}.`);
        const variableName = a.variableName !== undefined ? String(a.variableName) : (type === 'output' ? 'output' : 'input');

        const newCol: ColumnDefinition = { id: freshId('c'), label, type, dataType, variableName };
        const columns = [...model.columns];
        if (type === 'input') {
          const firstOutputIndex = columns.findIndex((c) => c.type === 'output');
          if (firstOutputIndex === -1) columns.push(newCol);
          else columns.splice(firstOutputIndex, 0, newCol);
        } else {
          columns.push(newCol);
        }
        // New cells default to '' on every rule (empty = wildcard, always valid).
        const rules = model.rules.map((r) => ({ ...r, cells: { ...r.cells, [newCol.id]: '' } }));
        return applyIfValid(model, { ...model, columns, rules }, access.setModel, { id: newCol.id });
      }

      case 'update_column': {
        const id = a.id as string;
        const target = model.columns.find((c) => c.id === id);
        if (!target) return reject(`No column with id "${String(id)}".`);
        const patch: Partial<ColumnDefinition> = {};
        if (a.label !== undefined) {
          if (typeof a.label !== 'string' || a.label.trim() === '') return reject('label must be a non-empty string.');
          patch.label = a.label;
        }
        if (a.dataType !== undefined) {
          if (!DATA_TYPES.includes(a.dataType as DataType)) return reject(`Unknown dataType "${String(a.dataType)}".`);
          patch.dataType = a.dataType as DataType;
        }
        if (a.type !== undefined) {
          if (!COLUMN_TYPES.includes(a.type as ColumnType)) return reject(`Unknown column type "${String(a.type)}".`);
          patch.type = a.type as ColumnType;
        }
        if (a.variableName !== undefined) patch.variableName = String(a.variableName);

        const columns = model.columns.map((c) => (c.id === id ? { ...c, ...patch } : c));
        const next = { ...model, columns };
        // Changing dataType/type can invalidate existing cells — run the REAL validator and
        // REJECT (no setModel) if the change introduces new errors.
        return applyIfValid(model, next, access.setModel, { id });
      }

      case 'delete_column': {
        const id = a.id as string;
        if (!model.columns.some((c) => c.id === id)) return reject(`No column with id "${String(id)}".`);
        const columns = model.columns.filter((c) => c.id !== id);
        const rules = model.rules.map((r) => {
          const cells = { ...r.cells };
          delete cells[id];
          return { ...r, cells };
        });
        return applyIfValid(model, { ...model, columns, rules }, access.setModel, { deleted: id });
      }

      case 'add_rule': {
        const cellsIn = (a.cells as Record<string, unknown>) ?? {};
        const cells: Record<string, string> = {};
        model.columns.forEach((c) => (cells[c.id] = ''));
        for (const [colId, raw] of Object.entries(cellsIn)) {
          const col = model.columns.find((c) => c.id === colId);
          if (!col) return reject(`Rule references unknown column id "${colId}".`);
          const value = raw == null ? '' : String(raw);
          if (!validateCell(value, col.dataType, col.type)) {
            return reject(`Value "${value}" is invalid for column "${col.label}" (${col.dataType}).`);
          }
          cells[colId] = value;
        }
        const newRule: Rule = { id: freshId('r'), cells };
        return applyIfValid(model, { ...model, rules: [...model.rules, newRule] }, access.setModel, { id: newRule.id });
      }

      case 'update_rule': {
        const id = a.id as string;
        const target = model.rules.find((r) => r.id === id);
        if (!target) return reject(`No rule with id "${String(id)}".`);
        const cellsIn = a.cells as Record<string, unknown>;
        if (!cellsIn || typeof cellsIn !== 'object') return reject('cells must be an object of columnId -> value.');
        const nextCells = { ...target.cells };
        for (const [colId, raw] of Object.entries(cellsIn)) {
          const col = model.columns.find((c) => c.id === colId);
          if (!col) return reject(`Update references unknown column id "${colId}".`);
          const value = raw == null ? '' : String(raw);
          if (!validateCell(value, col.dataType, col.type)) {
            return reject(`Value "${value}" is invalid for column "${col.label}" (${col.dataType}).`);
          }
          nextCells[colId] = value;
        }
        const rules = model.rules.map((r) => (r.id === id ? { ...r, cells: nextCells } : r));
        return applyIfValid(model, { ...model, rules }, access.setModel, { id });
      }

      case 'delete_rule': {
        const id = a.id as string;
        if (!model.rules.some((r) => r.id === id)) return reject(`No rule with id "${String(id)}".`);
        return applyIfValid(model, { ...model, rules: model.rules.filter((r) => r.id !== id) }, access.setModel, { deleted: id });
      }

      case 'set_hit_policy': {
        const hp = a.hitPolicy as HitPolicy;
        if (!HIT_POLICIES.includes(hp)) return reject(`Unknown hit policy "${String(a.hitPolicy)}". Valid: ${HIT_POLICIES.join(', ')}.`);
        return applyIfValid(model, { ...model, hitPolicy: hp }, access.setModel, { hitPolicy: hp });
      }

      case 'validate': {
        const v = validateModel(model);
        const invalid = Object.keys(v.invalidCells).map((key) => {
          // key format: `${ruleId}-${colId}` — split on the last '-' that begins the column id.
          const dash = key.lastIndexOf('-');
          return { ruleId: key.slice(0, dash), columnId: key.slice(dash + 1) };
        });
        return ok({ isValid: v.isValid, errorCount: v.errorCount, invalidCells: invalid });
      }

      case 'replace_model': {
        const candidate = a.model as unknown;
        // 1) Structural guard: ids/types/column-refs AND every cell value must be a string
        //    (a non-string cell would crash validateCell(...).trim()).
        const err = validateModelShape(candidate);
        if (err) return reject(err);
        // 2) Run the REAL validator on the candidate; reject if any cell value is invalid.
        const nextModel = candidate as DmnModel;
        const v = validateModel(nextModel);
        if (!v.isValid) {
          const reasons = describeInvalidCells(nextModel, v.invalidCells);
          return reject(`Replacement rejected: model has ${v.errorCount} invalid cell(s): ${reasons.join('; ')}.`);
        }
        access.setModel(nextModel);
        return ok(compactModel(nextModel));
      }

      default:
        return reject(`Unknown tool "${toolName}".`);
    }
  };

  return { kind: 'dmn', guidelines, tools, invoke };
}

/** Structural guard for replace_model — rejects malformed JSON before it can corrupt the editor. */
function validateModelShape(value: unknown): string | null {
  if (typeof value !== 'object' || value === null) return 'model must be a JSON object.';
  const m = value as Record<string, unknown>;
  if (typeof m.id !== 'string') return 'model.id must be a string.';
  if (typeof m.name !== 'string') return 'model.name must be a string.';
  if (!HIT_POLICIES.includes(m.hitPolicy as HitPolicy)) return `model.hitPolicy must be one of: ${HIT_POLICIES.join(', ')}.`;
  if (!Array.isArray(m.columns)) return 'model.columns must be an array.';
  if (!Array.isArray(m.rules)) return 'model.rules must be an array.';

  const colIds = new Set<string>();
  for (const c of m.columns as unknown[]) {
    if (typeof c !== 'object' || c === null) return 'each column must be an object.';
    const col = c as Record<string, unknown>;
    if (typeof col.id !== 'string') return 'each column needs a string id.';
    if (colIds.has(col.id)) return `duplicate column id "${col.id}".`;
    colIds.add(col.id);
    if (typeof col.label !== 'string') return `column "${col.id}" needs a string label.`;
    if (!COLUMN_TYPES.includes(col.type as ColumnType)) return `column "${col.id}" has invalid type.`;
    if (!DATA_TYPES.includes(col.dataType as DataType)) return `column "${col.id}" has invalid dataType.`;
  }
  for (const r of m.rules as unknown[]) {
    if (typeof r !== 'object' || r === null) return 'each rule must be an object.';
    const rule = r as Record<string, unknown>;
    if (typeof rule.id !== 'string') return 'each rule needs a string id.';
    if (typeof rule.cells !== 'object' || rule.cells === null) return `rule "${rule.id}" needs a cells object.`;
    for (const [key, cellValue] of Object.entries(rule.cells as Record<string, unknown>)) {
      if (!colIds.has(key)) return `rule "${rule.id}" references unknown column id "${key}".`;
      // Cell values MUST be strings — validateCell calls .trim(); a non-string would throw.
      if (typeof cellValue !== 'string') return `rule "${rule.id}" cell for column "${key}" must be a string.`;
    }
  }
  return null;
}
